import React, { useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Draggable from 'react-draggable';
import { Server, Trash2, Edit3 } from 'lucide-react';
import { Rack, PlacedEquipment } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import RackEquipmentSelector from './RackEquipmentSelector';
import { getRequestTheme } from '../Stock/requests/theme';
import { canvasIconGlyphSize, canvasIconSizePx } from '../../utils/canvasIcon';

interface RackComponentProps {
  rack: Rack;
}

const RackComponent: React.FC<RackComponentProps> = ({ rack }) => {
  const {
    setSelectedRack,
    setRackBuilderOpen,
    updateRackPosition,
    removeRack,
    updateRack,
    isCableRunMode,
    selectedSourceEquipment,
    setSelectedSourceEquipment,
    createCableRun,
    setSelectedEquipmentForPortView,
    setPortVisualizationOpen,
    setSelectedEquipment,
    setSelectedCable,
    setSelectedRackEquipment,
    toggleRackExistingStatus,
    alwaysShowExpandedEquipment,
    expandBadgesOnHover,
    appTheme,
    setEquipmentDragActive,
    gridSize,
    iconGridCells,
  } = useProjectStore(
    useShallow((s) => ({
      setSelectedRack: s.setSelectedRack,
      setRackBuilderOpen: s.setRackBuilderOpen,
      updateRackPosition: s.updateRackPosition,
      removeRack: s.removeRack,
      updateRack: s.updateRack,
      isCableRunMode: s.isCableRunMode,
      selectedSourceEquipment: s.selectedSourceEquipment,
      setSelectedSourceEquipment: s.setSelectedSourceEquipment,
      createCableRun: s.createCableRun,
      setSelectedEquipmentForPortView: s.setSelectedEquipmentForPortView,
      setPortVisualizationOpen: s.setPortVisualizationOpen,
      setSelectedEquipment: s.setSelectedEquipment,
      setSelectedCable: s.setSelectedCable,
      setSelectedRackEquipment: s.setSelectedRackEquipment,
      toggleRackExistingStatus: s.toggleRackExistingStatus,
      alwaysShowExpandedEquipment: s.alwaysShowExpandedEquipment,
      expandBadgesOnHover: s.expandBadgesOnHover,
      appTheme: s.appTheme,
      setEquipmentDragActive: s.setEquipmentDragActive,
      gridSize: s.gridSize,
      iconGridCells: s.iconGridCells,
    }))
  );

  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const iconSize = canvasIconSizePx(gridSize, iconGridCells);
  const glyphSize = canvasIconGlyphSize(iconSize);

  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(rack.name);
  const [showEquipmentSelector, setShowEquipmentSelector] = useState(false);
  const [livePos, setLivePos] = useState(rack.position);
  const hoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (alwaysShowExpandedEquipment && !isDragging) {
      setIsHovered(true);
    }
  }, [alwaysShowExpandedEquipment, isDragging]);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimer.current) clearTimeout(hoverLeaveTimer.current);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (hasBeenDragged) {
      setHasBeenDragged(false);
      return;
    }

    e.stopPropagation();

    if (isCableRunMode) {
      setShowEquipmentSelector(true);
      return;
    }

    setSelectedEquipment(null);
    setSelectedCable(null);
    setSelectedRackEquipment('', null);

    setSelectedRack(rack);
    setRackBuilderOpen(true);
  };

  const handleEquipmentSelect = (equipmentId: string) => {
    const equipment = rack.equipment.find(re => re.equipment.id === equipmentId);
    if (!equipment) return;

    const rackCenterX = rack.position.x + iconSize / 2;
    const rackCenterY = rack.position.y + iconSize / 2;

    const placedEquipment: PlacedEquipment = {
      id: `${rack.id}:${equipment.equipment.id}:${rack.equipment.indexOf(equipment)}`,
      equipment: equipment.equipment,
      position: { x: rackCenterX, y: rackCenterY }
    };

    const destHasMultiplePorts = (equipment.equipment.specifications?.ports || 1) > 1;
    if (selectedSourceEquipment) {
      if (destHasMultiplePorts) {
        setShowEquipmentSelector(false);
        handleViewPorts(placedEquipment);
      } else {
        const placedId = `${rack.id}:${equipment.equipment.id}:${rack.equipment.indexOf(equipment)}`;
        createCableRun(selectedSourceEquipment.id, placedId, 'ethernet');
        setSelectedSourceEquipment(null);
        setShowEquipmentSelector(false);
      }
    } else {
      setShowEquipmentSelector(false);
      if (destHasMultiplePorts) {
        handleViewPorts(placedEquipment);
      } else {
        setSelectedSourceEquipment(placedEquipment);
      }
    }
  };

  const handleEquipmentSelectorClose = () => {
    setShowEquipmentSelector(false);
    setSelectedSourceEquipment(null);
  };

  const handleViewPorts = (equipment: PlacedEquipment) => {
    setSelectedEquipmentForPortView(equipment);
    setPortVisualizationOpen(true);
  };

  useEffect(() => {
    if (!isDragging) setLivePos(rack.position);
  }, [rack.position, isDragging]);

  const handleDrag = (_e: any, data: any) => {
    setHasBeenDragged(true);
    setLivePos({ x: data.x, y: data.y });
  };

  const handleStart = () => {
    setHasBeenDragged(false);
    setIsDragging(true);
    setIsHovered(false);
    setEquipmentDragActive(true);
    clearHoverLeaveTimer();
    try { document.body.style.cursor = 'grabbing'; } catch {}
  };

  const handleStop = (_e: any, data: any) => {
    setIsDragging(false);
    setIsHovered(false);
    setEquipmentDragActive(false);
    updateRackPosition(rack.id, { x: data.x, y: data.y });
    setLivePos({ x: data.x, y: data.y });
    try { document.body.style.cursor = ''; } catch {}
  };

  useEffect(() => {
    const restore = () => { try { document.body.style.cursor = ''; } catch {} };
    window.addEventListener('mouseup', restore);
    window.addEventListener('pointerup', restore as any);
    window.addEventListener('blur', restore);
    document.addEventListener('visibilitychange', restore);
    return () => {
      window.removeEventListener('mouseup', restore);
      window.removeEventListener('pointerup', restore as any);
      window.removeEventListener('blur', restore);
      document.removeEventListener('visibilitychange', restore);
    };
  }, []);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete ${rack.name}? This will also remove all cables connected to this rack.`)) {
      removeRack(rack.id);
    }
  };

  const handleNameEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    if (editedName.trim() !== '' && editedName !== rack.name) {
      updateRack(rack.id, { name: editedName.trim() });
    }
    setIsEditingName(false);
  };

  const handleNameCancel = () => {
    setEditedName(rack.name);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  useEffect(() => {
    setEditedName(rack.name);
  }, [rack.name]);

  const clearHoverLeaveTimer = () => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  };

  const handleCardMouseEnter = () => {
    if (isDragging) return;
    clearHoverLeaveTimer();
    if (expandBadgesOnHover) {
      setIsHovered(true);
    }
  };

  const handleCardMouseLeave = (e: React.MouseEvent) => {
    // Ignore leave events that are still inside the card (e.g. crossing children during expand)
    const next = e.relatedTarget as Node | null;
    if (next && cardRef.current?.contains(next)) return;

    if (alwaysShowExpandedEquipment) return;

    // Small delay prevents expand/collapse flicker while the card is resizing under the cursor
    clearHoverLeaveTimer();
    hoverLeaveTimer.current = setTimeout(() => {
      setIsHovered(false);
      setIsEditingName(false);
    }, 120);
  };

  const unitHeight = 14;
  const rackBodyHeight = rack.size * unitHeight;
  const collapsedSize = iconSize;
  const expandedWidth = 180;
  // +4px so border-box border (2 top + 2 bottom) doesn't clip the last U
  const expandedHeight = collapsedSize + rackBodyHeight + 4;

  // Never expand while dragging — only on genuine hover (or always-expanded setting)
  const isExpanded = !isDragging && (isHovered || alwaysShowExpandedEquipment);

  const rackFrameBg = isDark ? '#171a1f' : '#e5e7eb';
  const rackUnitLine = isDark ? '#2b3140' : '#d1d5db';
  const rackUnitNum = isDark ? '#6b7280' : '#6b7280';

  return (
    <>
      <Draggable
        position={livePos}
        onStart={handleStart}
        onDrag={handleDrag}
        onStop={handleStop}
        handle=".rack-handle"
        positionOffset={{ x: 0, y: 0 }}
      >
        <div
          className="absolute"
          style={{ zIndex: isExpanded ? 40 : 10 }}
          onPointerDown={(e) => {
            try { (e.target as Element).setPointerCapture && (e.target as any).setPointerCapture((e as any).pointerId); } catch {}
          }}
          onPointerUp={(e) => {
            try { (e.target as Element).releasePointerCapture && (e.target as any).releasePointerCapture((e as any).pointerId); } catch {}
          }}
        >
          {/*
            Expand down + right only (no translate). Moving the card under the cursor
            caused rapid hover enter/leave flicker with top-* badge anchors.
          */}
          <div
            ref={cardRef}
            className={`rack-handle relative select-none transition-[width,height] duration-300 ease-out ${
              isDragging ? 'cursor-grabbing' : 'cursor-pointer'
            } ${
              isCableRunMode && isHovered ? 'ring-1 ring-purple-400 animate-pulse' : ''
            } ${rack.isExisting ? 'opacity-75' : ''}`}
            style={{
              width: isExpanded ? expandedWidth : collapsedSize,
              height: isExpanded ? expandedHeight : collapsedSize,
              touchAction: 'none',
            }}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
            onClick={handleClick}
            onContextMenu={(e) => {
              e.preventDefault();
              toggleRackExistingStatus(rack.id);
            }}
            title={`${rack.name}${rack.isExisting ? ' (Existing)' : ''}`}
          >
            {/* Badges outside overflow so they aren't clipped */}
            {rack.isExisting && (
              <div className="absolute -top-1.5 -left-1.5 bg-green-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold z-30 leading-none pointer-events-none">
                E
              </div>
            )}
            {!isExpanded && rack.equipment.length > 0 && (
              <div className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] bg-blue-500 rounded-full flex items-center justify-center z-30 shadow-sm pointer-events-none">
                <span
                  className="text-[10px] font-bold text-white tabular-nums"
                  style={{ lineHeight: 1 }}
                >
                  {rack.equipment.length}
                </span>
              </div>
            )}

            <div
              className="absolute inset-0 rounded-lg border-2 overflow-hidden box-border"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: isDark ? '#343a43' : '#334155',
                color: theme.text,
              }}
            >
            {/* Header row: icon + name/actions */}
            <div
              className="relative flex items-center flex-shrink-0"
              style={{ height: collapsedSize, width: '100%' }}
            >
              {/* Icon — centered in the collapsed tile at any icon size */}
              <div
                className="absolute top-0 left-0 flex items-center justify-center pointer-events-none"
                style={{
                  width: collapsedSize,
                  height: collapsedSize,
                  transform: 'translate(-2px, -2px)',
                }}
              >
                <Server
                  size={glyphSize}
                  strokeWidth={2}
                  style={{ color: isDark ? '#e5e7eb' : '#ffffff', display: 'block' }}
                />
              </div>

              <div
                className={`flex items-center flex-1 min-w-0 pr-2 gap-1 transition-opacity duration-200 ${
                  isExpanded ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ height: collapsedSize, paddingLeft: collapsedSize }}
              >
                <div className="flex-1 min-w-0">
                  {isEditingName ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={handleNameKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full text-xs font-medium px-1.5 py-0.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{
                        backgroundColor: theme.inputBg,
                        borderColor: theme.border,
                        color: theme.text,
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      className="group flex items-center min-w-0 max-w-full text-xs font-medium"
                      style={{ color: theme.text }}
                      onClick={handleNameEdit}
                      title={rack.name}
                    >
                      <span className="truncate">{rack.name}</span>
                      <Edit3 size={10} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" style={{ color: theme.muted }} />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="p-0.5 rounded transition-colors flex-shrink-0"
                  style={{ color: theme.muted }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.25)' : '#fee2e2')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                  title="Delete Rack"
                >
                  <Trash2 size={12} />
                </button>
                <span className="text-[10px] font-medium flex-shrink-0" style={{ color: theme.muted }}>
                  {rack.size}U
                </span>
              </div>
            </div>

            {/* Rack body — always in layout so height animation is stable; clipped when collapsed */}
            <div
              className="relative w-full"
              style={{
                height: rackBodyHeight,
                backgroundColor: rackFrameBg,
                borderTop: `1px solid ${theme.border}`,
              }}
            >
              {Array.from({ length: rack.size - 1 }, (_, i) => (
                <div
                  key={i}
                  className="absolute w-full"
                  style={{
                    top: (i + 1) * unitHeight,
                    borderTop: `1px solid ${rackUnitLine}`,
                  }}
                />
              ))}

              {Array.from({ length: rack.size }, (_, i) => (
                <div
                  key={`unit-${i}`}
                  className="absolute left-1 text-[9px] font-mono flex items-center"
                  style={{
                    top: i * unitHeight,
                    height: unitHeight,
                    color: rackUnitNum,
                  }}
                >
                  {i + 1}
                </div>
              ))}

              {rack.equipment.map((rackEq, index) => {
                const equipmentHeight = (rackEq.equipment.units || 1) * unitHeight - 2;
                const equipmentTop = rackEq.startUnit * unitHeight + 1;
                const key = rackEq.instanceId || `${rackEq.equipment.id}-${rackEq.startUnit}-${index}`;

                return (
                  <div
                    key={key}
                    className={`absolute left-5 right-1.5 text-white px-1 rounded border flex flex-col justify-center cursor-pointer transition-colors ${
                      rackEq.equipment.isExisting ? 'opacity-75' : ''
                    }`}
                    style={{
                      top: equipmentTop,
                      height: Math.max(equipmentHeight, 12),
                      minHeight: 12,
                      backgroundColor: '#2563eb',
                      borderColor: '#1d4ed8',
                      pointerEvents: isExpanded ? 'auto' : 'none',
                    }}
                    title={`${rackEq.equipment.name} (${rackEq.equipment.units || 1}U) - Click to view ports${rackEq.equipment.isExisting ? ' (Existing)' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const placedEquipment: PlacedEquipment = {
                        id: `${rack.id}:${rackEq.equipment.id}:${index}`,
                        equipment: rackEq.equipment,
                        position: {
                          x: rack.position.x + collapsedSize / 2,
                          y: rack.position.y + collapsedSize / 2
                        }
                      };
                      handleViewPorts(placedEquipment);
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2563eb')}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const updatedEquipment = rack.equipment.map((req, idx) =>
                        idx === index
                          ? { ...req, equipment: { ...req.equipment, isExisting: !req.equipment.isExisting } }
                          : req
                      );
                      updateRack(rack.id, { equipment: updatedEquipment });
                    }}
                  >
                    {rackEq.equipment.isExisting && (
                      <div className="absolute -top-1 -right-1 bg-green-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center font-bold z-10">
                        E
                      </div>
                    )}
                    <div className="truncate text-white text-[10px] leading-tight">
                      {rackEq.equipment.name || 'Unknown Equipment'}
                    </div>
                    {rackEq.equipment.units && rackEq.equipment.units > 1 && (
                      <div className="text-blue-100 text-[9px] leading-tight">{rackEq.equipment.units}U</div>
                    )}
                  </div>
                );
              })}

              {rack.equipment.length === 0 && (
                <div
                  className="absolute inset-0 flex items-center justify-center text-[10px]"
                  style={{ color: theme.muted }}
                >
                  Empty
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </Draggable>

      {showEquipmentSelector && (
        <RackEquipmentSelector
          rack={rack}
          onSelect={handleEquipmentSelect}
          onClose={handleEquipmentSelectorClose}
          isSelectingSource={!selectedSourceEquipment}
        />
      )}
    </>
  );
};

export default React.memo(RackComponent);
